/**
 * SiteMapBackground — map-backed container for the site Structure card.
 *
 * Fills the whole structure-details area with an OpenStreetMap (Leaflet)
 * map of the site and floats the supplied detail content over it inside a
 * translucent, blurred "glass" panel, so the map reads as the backdrop
 * while the fields stay legible. An expand control opens the location in a
 * larger interactive map dialog. Renders children plainly (no map) when
 * the site lacks usable coordinates.
 *
 * Contains:
 * - SiteMapBackground — the map backdrop + glass overlay + expand dialog (default export)
 */
import 'leaflet/dist/leaflet.css';

import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { alpha, Box, Dialog, IconButton } from '@mui/material';
import L from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_ZOOM = 15;

// Layout mode switch: map-backed details (true) vs the plain detail list
// (false) — flip here to compare the two treatments. Exported so the rail can
// add the structure/unit divider the map backdrop otherwise provides visually.
export const USE_MAP_BACKDROP = false;

// A site at exactly 0,0 means "no coordinates set", not the Gulf of Guinea.
const hasUsableCoords = (lat, lon) =>
  lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon)) && !(lat == 0 && lon == 0);

// App-styled location pin — a filled circle with a white ring, matching the
// dashboard map markers rather than Leaflet's default image pin (which would
// also need the webpack icon-url shim).
const buildMarkerIcon = () =>
  L.divIcon({
    className: 'site-map-marker',
    html:
      '<div style="width:16px;height:16px;border-radius:50%;background:var(--md-primary);' +
      'border:3px solid var(--color-white);box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

/**
 * Imperative Leaflet point map. Static (no pan/zoom) when not interactive —
 * used as the backdrop; fully interactive in the expand dialog.
 *
 * @param {object}  props
 * @param {number}  props.lat
 * @param {number}  props.lon
 * @param {boolean} [props.interactive]
 * @param {string|number} props.height
 */
function PointMap({ lat, lon, interactive = false, height }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      zoomControl: interactive,
      attributionControl: false,
      dragging: interactive,
      scrollWheelZoom: false,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      touchZoom: interactive,
    }).setView([Number(lat), Number(lon)], DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    L.marker([Number(lat), Number(lon)], { icon: buildMarkerIcon() }).addTo(map);

    mapRef.current = map;
    // The container is often sized after mount (rail/drawer/dialog
    // transitions); recompute once laid out so tiles fill the box.
    const sizer = setTimeout(() => map.invalidateSize(), 150);

    return () => {
      clearTimeout(sizer);
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lon, interactive]);

  return <Box ref={containerRef} sx={{ height, width: '100%' }} />;
}

/**
 * @param {object}     props
 * @param {number}     props.latitude
 * @param {number}     props.longitude
 * @param {React.node} props.children   - detail content floated over the map
 */
export default function SiteMapBackground({ latitude, longitude, children }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!USE_MAP_BACKDROP || !hasUsableCoords(latitude, longitude)) return children;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Map backdrop — absolutely fills the whole area behind the content. */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <PointMap lat={latitude} lon={longitude} height="100%" />
      </Box>
      {/* Light top scrim so the expand control reads against bright tiles. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background: theme =>
            `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.22)} 0%, ${alpha(theme.palette.common.black, 0)} 52px)`,
        }}
      />
      <IconButton
        size="small"
        onClick={() => setExpanded(true)}
        aria-label={t('sites.viewMap', 'View map')}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 3,
          color: 'common.white',
          bgcolor: theme => alpha(theme.palette.common.black, 0.45),
          '&:hover': { bgcolor: theme => alpha(theme.palette.common.black, 0.65) },
        }}
      >
        <OpenInFullIcon fontSize="small" />
      </IconButton>

      {/* Glass overlay — the contrasted, frosted panel the fields sit in. A
          top margin reveals a band of map (with the marker) above it. */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: '40px',
          mx: 'var(--space-2)',
          mb: 'var(--space-2)',
          borderRadius: 'var(--radius-lg)',
          border: theme => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          bgcolor: theme => alpha(theme.palette.background.paper, 0.86),
          backdropFilter: 'blur(8px)',
          boxShadow: 'var(--shadow-dropdown)',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>

      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '70vh' } }}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          {expanded ? <PointMap lat={latitude} lon={longitude} interactive height="100%" /> : null}
          <IconButton
            onClick={() => setExpanded(false)}
            aria-label={t('actions.close', 'Close')}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1200, bgcolor: 'background.paper', boxShadow: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
}
